import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { setConsentFallback } from '../lib/analytics';

const CONSENT_STORAGE_KEY = 'wizantix_cookie_consent';

type ConsentChoice = 'accepted' | 'declined';

const parseConsentChoice = (value: string | null): ConsentChoice | null => {
  if (value === 'accepted' || value === 'declined') {
    return value;
  }

  return null;
};

declare global {
  interface WindowEventMap {
    'cookie-consent-updated': CustomEvent<{ analytics: boolean; source: 'init' | 'user' }>;
  }
}

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = React.useState(false);

  const broadcastConsent = React.useCallback((choice: ConsentChoice, source: 'init' | 'user'): void => {
    window.dispatchEvent(
      new CustomEvent('cookie-consent-updated', {
        detail: { analytics: choice === 'accepted', source },
      })
    );

    if (source !== 'user') {
      return;
    }

    const eventName = choice === 'accepted' ? 'consent_granted' : 'consent_declined';
    const dataLayer = (window as Window & { dataLayer?: Array<Record<string, unknown>> }).dataLayer;
    if (Array.isArray(dataLayer)) {
      dataLayer.push({
        event: eventName,
        section: 'cookie_banner',
        action: choice === 'accepted' ? 'accept' : 'decline',
        target: 'analytics',
        path: window.location.pathname,
      });
    }
  }, []);

  React.useEffect(() => {
    let storedConsent: ConsentChoice | null = null;
    try {
      storedConsent = parseConsentChoice(localStorage.getItem(CONSENT_STORAGE_KEY));
    } catch {
      storedConsent = null;
    }

    if (!storedConsent) {
      setVisible(true);
      return;
    }

    broadcastConsent(storedConsent, 'init');
  }, [broadcastConsent]);

  const handleAccept = (): void => {
    setConsentFallback('accepted');

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
    } catch {
      // Storage may be unavailable; keep consent for this session.
    }

    broadcastConsent('accepted', 'user');
    setVisible(false);
  };

  const handleDecline = (): void => {
    setConsentFallback('declined');

    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, 'declined');
    } catch {
      // Storage may be unavailable; keep decline for this session.
    }

    broadcastConsent('declined', 'user');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-4 left-4 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-2xl border border-border bg-gradient-to-br from-card to-background/95 p-5 shadow-2xl backdrop-blur md:bottom-6 md:left-6"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          data-analytics-explicit="true"
        >
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary-300 ring-1 ring-primary/30">
              <ShieldCheck size={18} />
            </div>
            <h3 id="cookie-banner-title" className="text-sm font-semibold text-foreground">
              Your Privacy
            </h3>
          </div>

          <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
            We use analytics to understand how people use Wizantix. No data is sold.{' '}
            <a
              href="https://wizantix.com/privacy-policy#privacy-cookies"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary hover:underline"
            >
              Learn more
            </a>
          </p>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleDecline}
              className="flex-1 rounded-lg border border-border bg-background/60 px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-muted-foreground/40 hover:text-foreground"
            >
              Decline
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-400"
            >
              Accept Analytics
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;